import type { Member, Payment } from '@/types';
import {
  MONTHLY_FEE,
  getPaymentSummary,
  getLastPayment,
  isPaidThisMonth,
  sortPaymentsByDateDesc,
} from '@/lib/payments';
import { getMembershipStatus } from '@/lib/utils';

export interface MonthlyCollectionPoint {
  month: string;
  shortMonth: string;
  collected: number;
  expected: number;
  membersPaid: number;
}

export interface MemberGrowthPoint {
  month: string;
  shortMonth: string;
  newMembers: number;
  totalMembers: number;
}

export interface MembershipBreakdown {
  active: number;
  completed: number;
  closed: number;
}

export interface UnpaidMemberRow {
  member: Member;
  monthsPaid: number;
  amountDue: number;
  lastPaidDate: string | null;
}

export interface RecentPaymentRow {
  id: string;
  memberName: string;
  memberId: string;
  amount: number;
  paymentType: Payment['paymentType'];
  paymentDate: string;
  month: string;
  year: number;
  monthsPaid: number;
}

export const getMonthlyCollectionTrend = (
  payments: Payment[],
  memberCount: number,
  months = 6
): MonthlyCollectionPoint[] => {
  const now = new Date();
  const points: MonthlyCollectionPoint[] = [];

  for (let i = months - 1; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthPayments = payments.filter((payment) => {
      const paymentDate = new Date(payment.paymentDate);
      return (
        paymentDate.getMonth() === date.getMonth() &&
        paymentDate.getFullYear() === date.getFullYear()
      );
    });

    points.push({
      month: date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
      shortMonth: date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }),
      collected: monthPayments.reduce((sum, payment) => sum + payment.amount, 0),
      expected: memberCount * MONTHLY_FEE,
      membersPaid: new Set(monthPayments.map((payment) => payment.memberId)).size,
    });
  }

  return points;
};

export const getTrendRangeLabel = (months = 6): string => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - (months - 1), 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 1);
  const format = (date: Date) =>
    date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
  return `(${format(start)} to ${format(end)})`;
};

export const getMemberGrowthTrend = (members: Member[], months = 6): MemberGrowthPoint[] => {
  const now = new Date();
  const points: MemberGrowthPoint[] = [];

  for (let i = months - 1; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);

    points.push({
      month: date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
      shortMonth: date.toLocaleDateString('en-IN', { month: 'short' }),
      newMembers: members.filter((member) => {
        const joinedDate = new Date(member.joinedDate);
        return (
          joinedDate.getMonth() === date.getMonth() &&
          joinedDate.getFullYear() === date.getFullYear()
        );
      }).length,
      totalMembers: members.filter(
        (member) => new Date(member.joinedDate) <= endOfMonth
      ).length,
    });
  }

  return points;
};

export const getMembershipBreakdown = (
  members: Member[],
  payments: Payment[]
): MembershipBreakdown => {
  const breakdown: MembershipBreakdown = { active: 0, completed: 0, closed: 0 };

  members.forEach((member) => {
    const monthsPaid = payments.filter((payment) => payment.memberId === member.id).length;
    breakdown[getMembershipStatus(member, monthsPaid)] += 1;
  });

  return breakdown;
};

export const getLifetimeCollected = (payments: Payment[]): number =>
  payments.reduce((sum, payment) => sum + payment.amount, 0);

export const getTotalOutstanding = (members: Member[], payments: Payment[]): number =>
  members.reduce((sum, member) => {
    const memberPayments = payments.filter((payment) => payment.memberId === member.id);
    return sum + getPaymentSummary(memberPayments).amountDue;
  }, 0);

export const getUnpaidActiveMembers = (
  members: Member[],
  payments: Payment[],
  limit = 8
): UnpaidMemberRow[] =>
  members
    .filter((member) => {
      const memberPayments = payments.filter((payment) => payment.memberId === member.id);
      return (
        getMembershipStatus(member, memberPayments.length) === 'active' &&
        !isPaidThisMonth(memberPayments)
      );
    })
    .map((member) => {
      const memberPayments = payments.filter((payment) => payment.memberId === member.id);
      const summary = getPaymentSummary(memberPayments);
      const lastPayment = getLastPayment(memberPayments);
      return {
        member,
        monthsPaid: summary.monthsPaid,
        amountDue: summary.amountDue,
        lastPaidDate: lastPayment?.paymentDate ?? null,
      };
    })
    .slice(0, limit);

export const getRecentPayments = (
  payments: Payment[],
  members: Member[],
  limit = 10
): RecentPaymentRow[] =>
  sortPaymentsByDateDesc(payments)
    .slice(0, limit)
    .map((payment) => {
      const member = members.find((item) => item.id === payment.memberId);
      const monthsPaid = payments.filter((item) => item.memberId === payment.memberId).length;
      return {
        id: payment.id,
        memberName: member?.name ?? 'Unknown',
        memberId: payment.memberId,
        amount: payment.amount,
        paymentType: payment.paymentType,
        paymentDate: payment.paymentDate,
        month: payment.month,
        year: payment.year,
        monthsPaid,
      };
    });
